import React, { Component, ChangeEvent } from 'react';
import { connect } from 'react-redux';
import Honeybadger from '@honeybadger-io/js';

import { patient } from '../../../redux';
import PhotoUploadModal from '../components/PhotoUploadModal';
import IDUpload, { Container } from './components/IDUpload';
import {
  isValidFileExtension,
  showErrorValidExtensionToast
} from '../../../utilities/fileValidation';
import Success from '../components/Success';
import {
  uploadFile,
  CATEGORY,
  UploadFileException,
  UploadFileResponse
} from '../../../api/pf2/files';

type Props = {
  clearFrontImageError: () => void;
  onSubmittedID: () => void;
  onClose: () => void;
};

type State = {
  frontImage?: File;
  frontImageBlob?: string;
  side: 'front' | 'success';
  error: string | null;
  loading: boolean;
  loadingPercentage: number | null;
};

class IDUploadModal extends Component<Props, State> {
  _modalRef = React.createRef<PhotoUploadModal>();

  constructor(props: Props) {
    super(props);

    this.state = {
      error: null,
      loading: false,
      loadingPercentage: null,
      side: 'front'
    };
  }

  handleFileChange = (target: 'front', e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const files = e.target?.files;
    if (!files || !files[0]) {
      return;
    }

    if (!isValidFileExtension(files[0].type)) {
      return showErrorValidExtensionToast();
    }

    const newState: Pick<State, 'frontImage' | 'frontImageBlob'> = {
      [`${target}Image`]: files[0],
      [`${target}ImageBlob`]: URL.createObjectURL(files[0])
    };

    this.setState(newState);
  };

  handleFrontFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.handleFileChange('front', e);
  };

  onSubmit = async (target: 'front', next: 'success') => {
    this.setState({ loading: true });
    const file = this.state[`${target}Image`];

    try {
      const category = CATEGORY.ID_FRONT;

      if (!file) {
        this.setState({ loading: false, error: 'Something went wrong, please try again.' });
        return;
      }

      const response = await uploadFile(file, category);
      const responseJson: UploadFileResponse = await response.json();

      if ('errors' in responseJson && responseJson?.errors) {
        this.setState({ loading: false, error: responseJson.errors });
        return;
      }

      if (!response.ok) {
        Honeybadger.notify('IDUploadError', {
          message: `${response.status} ${response.statusText}: ${await response.text()}`
        });
        this.setState({ loading: false, error: 'Something went wrong, please try again.' });
        return;
      }

      if (target === 'front') this.props.clearFrontImageError();

      this.setState({ side: next, loading: false });
    } catch (err) {
      Honeybadger.notify(JSON.stringify(err), { name: 'IDUploadError' });

      const error = err as UploadFileException;
      this.setState({
        error: error?.message || 'Something went wrong, please try again.'
      });
    }
  };

  onSubmitFront = async () => {
    await this.onSubmit('front', 'success');
  };

  dismiss = () => {
    const { current } = this._modalRef;
    if (!current || !current.dismiss) return;
    current.dismiss();
  };

  onClose = () => {
    const { onSubmittedID, onClose } = this.props;
    const { frontImageBlob, side } = this.state;
    onClose();
    if (frontImageBlob && side === 'success') onSubmittedID();
  };

  renderSide = () => {
    const {
      frontImageBlob,
      // backImageBlob,
      loading,
      side,
      error,
      loadingPercentage,
      frontImage
      // backImage
    } = this.state;
    switch (side) {
      case 'front':
        return (
          <IDUpload
            label="Drag or click to upload the Front of your ID."
            image={frontImageBlob}
            type={frontImage?.type}
            onFileChange={this.handleFrontFileChange}
            onSubmit={this.onSubmitFront}
            onCancel={this.dismiss}
            loading={loading}
            error={error}
            loadingPercentage={loadingPercentage}
          />
        );
      default:
        return (
          <Container>
            <Success
              label="Your ID has been uploaded."
              onClose={this.dismiss}
              images={[frontImageBlob as string]}
              height={150}
              width={200}
            />
          </Container>
        );
    }
  };

  render() {
    return (
      <PhotoUploadModal ref={this._modalRef} onClose={this.onClose}>
        {this.renderSide()}
      </PhotoUploadModal>
    );
  }
}

export default connect(null, dispatch => ({
  clearFrontImageError: () => dispatch(patient.resetIDError('front')),
  onSubmittedID: () => dispatch(patient.updateSubmittedID(true))
}))(IDUploadModal);
